/**
 * @fileoverview イベントの一覧等に表示する用のカードレイアウトコンポーネント
 */
<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { cardCommonProps, defaultFunction } from '@/components/_parials/contentsCard'
import MaterialIcon from '@/components/_core/MaterialIcon.vue'
import { DATE_FORMAT_JA_WITH_DAY_OF_WEEK } from '@/constants/dateFormat'
import { localeDateJa } from '@/utilities/formatter'

export default defineComponent({
  name: 'EventCard',

  components: {
    MaterialIcon,
  },

  props: {
    ...cardCommonProps,

    item: {
      type: Object as PropType<SolutionEventItem>,
      required: true,
    },
  },

  setup(props) {
    const {
      // computed
      sizeStateClass,
      linkTo,
      highlightHeading,
      highlightDescription,
      wrapperTagName,
      computedHeadingOptions,
      computedImageOptions,
      cssVariables,
    } = defaultFunction(props)

    return {
      // constants
      DATE_FORMAT_JA_WITH_DAY_OF_WEEK,

      // computed
      sizeStateClass,
      linkTo,
      highlightHeading,
      highlightDescription,
      wrapperTagName,
      computedHeadingOptions,
      computedImageOptions,
      cssVariables,

      // methods
      localeDateJa,
    }
  },
})
</script>

<template>
  <component
    :is="wrapperTagName"
    v-if="item"
    :class="[
      'l-unit__eventCard',
      sizeStateClass,
    ]"
    :href="linkTo"
    :data-horizontal-pc="horizontal.pc"
    :data-horizontal-sp="horizontal.sp"
    :data-hero="hasHero && !!item.thumbImageUrl"
    :data-read="item.isRead"
    :style="cssVariables">
    <div class="l-unit__eventCard--header">
      <div
        :class="[
          'o-heading__eventCard',
          sizeStateClass,
        ]"
        :data-read="item.isRead">
        <component
          :is="computedHeadingOptions.tagName"
          class="o-heading__eventCard--item"
          :style="{
            'max-height': computedHeadingOptions.maxHeight,
            'line-height': computedHeadingOptions.lineHeight,
            'font-weight': computedHeadingOptions.fontWeight,
            '--font-size-pc': computedHeadingOptions.fontSize.pc,
            '--font-size-sp': computedHeadingOptions.fontSize.sp,
          }"
          v-html="highlightHeading" />
      <!-- /.o-heading__eventCard --></div>
    <!-- /.l-unit__eventCard header --></div>

    <div
      v-if="hasHero && item.thumbImageUrl"
      class="l-unit__eventCard--image">
      <img
        class="img"
        :src="item.thumbImageUrl"
        loading="lazy"
        alt="">
    <!-- /.l-unit__eventCard image --></div>

    <div
      v-if="item.description"
      class="l-unit__eventCard--description">
      <div class="o-label__description">
        <p
          class="o-label__description--item"
          v-html="highlightDescription" />
      <!-- /.o-label__description --></div>
    <!-- /.l-unit__eventCard description --></div>

    <div
      v-if="item.date && item.date.from && item.date.to"
      class="l-unit__eventCard--footer is-date">
      <div class="o-list__date">
        <dl class="o-list__date">
          <dt class="o-list__date--term">
            <span class="o-list__date--icon">
              <MaterialIcon name="CalendarMonthOutline" />
            </span>
            開催期間
          </dt>
          <dd class="o-list__date--description">
            <time
              class="time u-text__block"
              :datetime="item.date.from">
              {{ localeDateJa(item.date.from, DATE_FORMAT_JA_WITH_DAY_OF_WEEK) }}
            </time>
            〜
            <time
              class="time u-text__block"
              :datetime="item.date.to">
              {{ localeDateJa(item.date.to, DATE_FORMAT_JA_WITH_DAY_OF_WEEK) }}
            </time>
          </dd>
        </dl>
      <!-- /.o-list__date --></div>
    <!-- /.l-unit__eventCard footer --></div>
  <!-- /.l-unit__eventCard --></component>
</template>

<style lang="sass" scoped>
$icon-width: 1.4em  // same size as solution logo in SolutionName.vue
$icon-margin: 0.4em

// layout
.l-unit__eventCard
  $root: &
  text-decoration: none
  overflow: hidden
  margin-top: 1em
  +transition

  &:first-child
    margin-top: 0

  &[href]
    +mouseover
      .o-heading__eventCard--item
        text-decoration: underline

      #{$root}--image
        opacity: 0.5

  & &--image
    position: relative
    +transition

    > .img
      height: 100%
      display: block
      object-fit: cover
      position: absolute
      top: 50%
      left: 50%
      transform: translate(-50%, -50%)

  & &--description,
  & &--footer
    margin-top: 0.5em

  // Phablet 以上のとき
  +breakpoint($max: false, $min: true)
    // 縦型（PC）
    &[data-horizontal-pc="false"]
      $image-width: 238
      $image-height: 104
      $image-gap: 1em
      $date-gap: 0.5em
      --image-width-pc: #{$image-width}
      --image-height-pc: #{$image-height}
      --image-aspect-pc: #{math.div($image-height, $image-width)}
      display: grid
      grid-template-columns: 1fr
      grid-template-rows: auto 1fr auto

      #{$root}--header
        grid-row: 2 / 3
        margin-bottom: 0.5em

      #{$root}--image
        grid-row: 1 / 2
        margin-bottom: 0.5em

        &::before
          content: ''
          width: 0
          display: block
          padding-top: rate(var(--image-aspect-pc))

      #{$root}--footer
        &.is-date
          grid-row: 3 / 4

    // 横型（PC）
    &[data-horizontal-pc="true"]
      $image-width: 200
      $image-height: 100
      $image-gap: 1em
      $horizontal-gap: 0.5em
      --image-width-pc: #{$image-width}
      --image-height-pc: #{$image-height}
      display: grid
      grid-template-rows: 1fr auto

      &[data-hero="true"]
        grid-template-columns: auto $image-gap 1fr

      &[data-hero="false"]
        grid-template-columns: auto 0 1fr

      #{$root}--header
        grid-column: 3 / 4
        grid-row: 1 / 2

      #{$root}--image
        width: px(var(--image-width-pc))
        grid-column: 1 / 2
        grid-row: 1 / 3

        &::before
          content: ""
          width: 0
          display: block
          padding-top: px(var(--image-height-pc))

      #{$root}--footer
        &.is-date
          grid-column: 3 / 4
          grid-row: 2 / 3

  +breakpoint
    // 縦型（SP）
    &[data-horizontal-sp="false"]
      $image-width: 400
      $image-height: 200
      $image-gap: 1em
      $date-gap: 0.5em
      --image-width-pc: #{$image-width}
      --image-height-pc: #{$image-height}
      --image-aspect-pc: #{math.div($image-height, $image-width)}
      display: grid
      grid-template-columns: 1fr
      grid-template-rows: auto 1fr auto

      #{$root}--header
        grid-row: 2 / 3

      #{$root}--image
        grid-row: 1 / 2

        &::before
          content: ''
          width: 0
          display: block
          padding-top: rate(var(--image-aspect-pc))

      #{$root}--footer
        &.is-date
          grid-row: 3 / 4

    // 横型（SP）
    &[data-horizontal-sp="true"]
      $image-width: 136
      $image-height: 72
      $image-gap: 1em
      $horizontal-gap: 0.5em
      --image-width-pc: #{$image-width}
      --image-height-pc: #{$image-height}
      display: grid
      grid-template-rows: 1fr auto

      &[data-hero="true"]
        grid-template-columns: auto $image-gap 1fr

      &[data-hero="false"]
        grid-template-columns: auto 0 1fr

      #{$root}--header
        grid-column: 3 / 4
        grid-row: 1 / 2

      #{$root}--image
        width: px(var(--image-width-pc))
        grid-column: 1 / 2
        grid-row: 1 / 3

        &::before
          content: ""
          width: 0
          display: block
          padding-top: px(var(--image-height-pc))

      #{$root}--footer
        &.is-date
          grid-column: 3 / 4
          grid-row: 2 / 3

// object
.o-heading__eventCard
  $root: &
  --font-size-pc: $font-large
  --font-size-sp: $font-large

  & &--item
    overflow: hidden
    color: $font-black
    font-size: var(--font-size-pc)

  &[data-read="true"]
    #{$root}--item
      color: $font-light-gray

  +breakpoint
    #{$root}--item
      font-size: var(--font-size-sp)

.o-label__description
  $line-height: 1.6
  $max-line: 2
  & &--item
    max-height: #{$max-line * $line-height}em
    overflow: hidden
    line-height: $line-height
    color: $font-light-gray
    font-size: $font-small

.o-list__date
  $root: &
  margin-top: 1em

  &:first-child
    margin-top: 0

  & &--term
    display: flex
    align-items: center
    color: $font-light-gray
    font:
      size: $font-small
      weight: bold

  & &--icon
    width: 1em
    margin-right: 0.3em

  & &--description
    color: $font-black
    font-size: $font-small
    margin-top: 0.2em

    &:first-child
      margin-top: 0

  +breakpoint
    #{$root}--term
      font-size: $font-x-small

    #{$root}--description
      font-size: $font-x-small
</style>
